import { DictContext } from "@/layout/context/DictContext";
import { Logistics, LogisticsEvent, AddLogistics } from "@/types/shipment";
import { Flex, Modal, Tag, Typography, Form, Select, Input, InputNumber, Button, notification, Spin, DatePicker } from "antd";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import STableLayout from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import WMSAvatar from "@/styles/WMSAvatar";
import SelectShipMethod from "@/components/Selector/SelectShipMethod";
import SelectShipEvent from "@/components/Selector/SelectShipEvent";
import { logisticsFetch, logisticsUpdate, logisticsEventAdd, logisticsShip, orderStatusUpdate } from "@/apis/shipment";
import dayjs from "dayjs";
import { getUserName } from "@/utils/user";
import SelectCurrencyVal from "@/components/Selector/SelectCurrencyVal";

interface LogisticsDetailProps {
  open: boolean;
  logistics: Logistics;
  onClose: () => void;
  onSuccess: () => void;
  readonly?: boolean;
};

interface EventData extends LogisticsEvent {
  isAdd?: boolean;
}

const SIGNED_EVENT_STATUS = 8;  // 签收
const ABNORMAL_EVENT_STATUS = 9; // 异常
const SHIPPING_STATUS = 6;  // 运输中
const DELIVERED_STATUS = 7; // 全部送达

const LogisticsDetail: FC<LogisticsDetailProps> = ({ open, logistics, onClose, onSuccess, readonly = false }) => {
  const { t } = useTranslation();
  const { shipMethods, shipEvents, shipLogistics } = useContext(DictContext);
  const [form] = Form.useForm<AddLogistics>();
  const curUser = useSelector((state: RootState) => state.userReducer.curUser);

  const [loading, setLoading] = useState(false);
  const [detailLogistics, setDetailLogistics] = useState<Logistics | null>(null);
  const [events, setEvents] = useState<EventData[]>([]);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [hasItemUpdate, setHasItemUpdate] = useState(false);

  const methodInfo = shipMethods.find(m => m.key === detailLogistics?.shipMethod)?.desc ?? '';
  const statusInfo = shipLogistics.find(s => s.key === detailLogistics?.status)?.desc ?? '';

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const detail = await logisticsFetch(logistics.id);
        setDetailLogistics(detail);
        form.setFieldsValue({
          shipMethod: detail.shipMethod,
          carrier: detail.carrier,
          trackingNo: detail.trackingNo,
          blNo: detail.blNo,
          vesselVoyage: detail.vesselVoyage,
          containerNo: detail.containerNo,
          freightCost: detail.freightCost,
          freightCurrency: detail.freightCurrency,
          remark: detail.remark,
        });
        const eventList: EventData[] = (detail.events || []).map((event) => ({
          ...event,
        }));
        setEvents(eventList);
      } catch (error) {
        notification.error({ message: t('shipment.getPackErr') });
      } finally {
        setLoading(false);
      }
    };
    open && fetchDetail();
  }, [logistics.id, open, form, t]);

  const handleSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const newEvents = events.filter(event => event.isAdd); // 只处理新增事件
      const hasExistingEvents = events.length > newEvents.length; // 是否有已事件
      const hasAbnormal = newEvents.some(event => event.status === ABNORMAL_EVENT_STATUS); // 是否有异常事件
      const hasSigned = newEvents.some(event => event.status === SIGNED_EVENT_STATUS); // 是否有签收事件

      setLoading(true);
      if (hasUpdate) {
        await logisticsUpdate(detailLogistics!.id, values);
      }

      // 添加新事件
      for (const event of newEvents) {
        await logisticsEventAdd(detailLogistics!.id, {
          eventTime: event.eventTime,
          location: event.location,
          status: event.status,
          description: event.description,
        });
      }
      // 更新发货单状态
      if (newEvents.length > 0 && !hasAbnormal && (hasSigned || !hasExistingEvents)) {
        await orderStatusUpdate(
          detailLogistics!.shipmentOrderId,
          hasSigned ? DELIVERED_STATUS : SHIPPING_STATUS
        );
      }

      setLoading(false);
      notification.success({ message: t('shipment.saveOk') });
      setHasUpdate(false);
      setHasItemUpdate(false);
      onSuccess();
    } catch (error) {
      setLoading(false);
      notification.error({ message: t('shipment.saveFail') });
    }
  };

  const handleAddEvent = () => {
    const newEvent: EventData = {
      id: `add-${Date.now()}`,
      eventTime: dayjs().startOf('day').valueOf(),
      location: '',
      status: 1,
      description: '',
      createdAt: Date.now(),
      creator: curUser,
      updater: curUser,
      isAdd: true,
    };
    setEvents([...events, newEvent]);
  };

  const handleRemoveEvent = (key: string) => {
    setEvents(events.filter(e => e.id !== key));
  };

  const handleShip = async () => {
    try {
      setLoading(true);
      await logisticsShip(detailLogistics!.id);
      setLoading(false);
      notification.success({ message: t('shipment.shipOk') });
      const detail = await logisticsFetch(detailLogistics!.id);
      setDetailLogistics(detail);
      onSuccess();
    } catch (error) {
      setLoading(false);
      notification.error({ message: t('shipment.shipFail') });
    }
  };

  const handleEventFieldChange = (id: string, field: keyof EventData, value: any) => {
    setHasItemUpdate(true);
    setEvents(events.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const eventColumns: ColumnsType<EventData> = [
    {
      title: t('shipment.eventTime'),
      key: 'eventTime',
      width: 180,
      render: (_, record) => {
        if (record.isAdd) {
          return (
            <DatePicker
              style={{ width: '100%' }}
              value={record.eventTime ? dayjs(record.eventTime) : null}
              onChange={(date) => handleEventFieldChange(record.id, 'eventTime', date?.startOf('day').valueOf() || Date.now())}
            />
          );
        }
        return dayjs(record.eventTime).format('YYYY-MM-DD');
      },
    },
    {
      title: t('shipment.location'),
      key: 'location',
      width: 150,
      render: (_, record) => {
        if (record.isAdd) {
          return (
            <Input
              value={record.location}
              onChange={(e) => handleEventFieldChange(record.id, 'location', e.target.value)}
              placeholder={t('shipment.plLocation')}
            />
          );
        }
        return <SText text={record.location} />;
      },
    },
    {
      title: t('shipment.status'),
      key: 'status',
      width: 120,
      render: (_, record) => {
        if (record.isAdd) {
          return (
            <SelectShipEvent<number>
              value={record.status}
              onChange={(value) => handleEventFieldChange(record.id, 'status', value)}
              style={{ width: '100%' }}
            />
          );
        }
        const statusInfo = shipEvents.find(s => s.key === record.status);
        return statusInfo ? <SText text={statusInfo.desc} /> : '-';
      },
    },
    {
      title: t('shipment.description'),
      key: 'description',
      width: 250,
      render: (_, record) => {
        if (record.isAdd) {
          return (
            <Input
              value={record.description}
              onChange={(e) => handleEventFieldChange(record.id, 'description', e.target.value)}
              placeholder={t('shipment.plDescription')}
            />
          );
        }
        return <SText text={record.description} />;
      },
    },
    {
      title: t('shipment.creator'),
      key: 'creator',
      width: 200,
      render: (_, record) => {
        const user = record.isAdd ? curUser : record.creator;
        if (!user) return null;
        return (
          <>
            <WMSAvatar
              avatarId={user.avatar}
              uniChar={user.id}
              creator={user}
              size={32}
            />
            <span>{getUserName(user)}</span>
          </>
        );
      },
    },
    {
      title: t('common.opt'),
      key: 'action',
      width: 120,
      render: (_, record) => {
        if (record.isAdd) {
          return (
            <Button size="small" danger onClick={() => handleRemoveEvent(record.id)}>
              {t('common.del')}
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const Title = (
    <Flex gap={6} justify="space-between" style={{ width: '100%' }} align='center' >
      <Flex gap={6} align="center">
        <Typography.Text>{t('shipment.logistics')}</Typography.Text>
        <Tag>{methodInfo}</Tag>
        <Tag>{statusInfo}</Tag>
      </Flex>
      {detailLogistics?.status === 0 && (
        <Button type="primary" size="small" disabled={readonly} onClick={handleShip} loading={loading} style={{ marginRight: 34 }}>
          {t('shipment.doShip')}
        </Button>
      )}
    </Flex>
  );

  return (
    <Modal
      title={Title}
      width={1200}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okButtonProps={{ disabled: loading || (!hasUpdate && !hasItemUpdate) || readonly }}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      maskClosable={false}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form<AddLogistics> form={form} disabled={readonly} onValuesChange={() => setHasUpdate(true)}>
          <Flex gap={10} wrap>
            <Form.Item name="shipMethod" label={t('shipment.shipMethod')} style={{ flex: 1 }}>
              <SelectShipMethod disabled />
            </Form.Item>
            <Form.Item name="carrier" label={t('shipment.shipLogistics')} style={{ flex: 1 }}>
              <Input placeholder={t('shipment.plCarrier')} />
            </Form.Item>
            <Form.Item name="trackingNo" label={t('shipment.trackingNo')} style={{ flex: 1 }}>
              <Input placeholder={t('shipment.plTrackingNo')} />
            </Form.Item>
          </Flex>
          <Flex gap={10} wrap>
            <Form.Item name="blNo" label={t('shipment.blNo')} style={{ flex: 1 }}>
              <Input placeholder={t('shipment.plBlNo')} />
            </Form.Item>
            <Form.Item name="vesselVoyage" label={t('shipment.vesselVoyage')} style={{ flex: 1 }}>
              <Input placeholder={t('shipment.plVesselVoyage')} />
            </Form.Item>
            <Form.Item name="containerNo" label={t('shipment.containerNo')} style={{ flex: 1 }}>
              <Input placeholder={t('shipment.plContainerNo')} />
            </Form.Item>
          </Flex>
          <Flex gap={10} wrap>
            <Form.Item name="freightCost" label={t('shipment.freightCost')} style={{ flex: 1 }}>
              <InputNumber changeOnWheel style={{ width: '100%' }} min={0} placeholder={t('shipment.plFreightCost')} />
            </Form.Item>
            <Form.Item name="freightCurrency" label={t('shipment.freightCurrency')} style={{ flex: 1 }} normalize={value => value?.toUpperCase().replace(/[^A-Z]/g, '')}>
              <SelectCurrencyVal />
            </Form.Item>
          </Flex>
          <Form.Item name="remark" label={t('shipment.remark')}>
            <Input.TextArea rows={2} maxLength={1000} showCount placeholder={t('shipment.plRemark')} />
          </Form.Item>
        </Form>

        <Typography.Title level={5} style={{ margin: '16px 0 8px' }}>
          {t('shipment.shipEvents')}
        </Typography.Title>

        <STableLayout
          dataSource={events as AnyObject[]}
          columns={eventColumns as ColumnsType<AnyObject>}
          total={events.length}
          pagination={false}
          headerChildren={
            <Button type="primary" size="small" disabled={readonly} onClick={handleAddEvent}>
              {t('shipment.addEvent')}
            </Button>
          }
          extHeight={-200}
          scrollX={1000}
        />
      </Spin>
    </Modal>
  );
};

export default LogisticsDetail;
